// Issue rules tab (admin): row-level issue-detection rules.
// Part of the Reference page (components/Reference.jsx). Split out 2026-10-14.
import { useState, useEffect, useMemo } from 'react';
import { putJson, delJson, fetchJson } from '../../api.js';

// ============================================================================
//  IssueRulesPanel — admin-only. Edit row-level issue-detection rules.
//  Simple rules can be enabled/disabled, severity swapped, tunable params
//  (thresholds, customer lists) edited. System rules (complex fuzzy checks)
//  are read-only apart from enable/disable + severity.
//  Storage: kv_store namespace 'issue-rules' key 'primary' via
//  /api/settings/issue-rules.
// ============================================================================
export default function IssueRulesPanel() {
  // Data: the rule set (loaded/saved via /api/settings/issue-rules) and status messages.
  const [cfg, setCfg]           = useState(null);
  const [, setDefaults]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState(null);
  const [msg, setMsg]           = useState(null);
  const [busy, setBusy]         = useState(false);

  useEffect(() => { reload(); }, []);
  async function reload() {
    setLoading(true); setErr(null); setMsg(null);
    try {
      const r = await fetchJson('/api/settings/issue-rules');
      setCfg(r.config); setDefaults(r.defaults);
    } catch (e) { setErr(e.message || String(e)); }
    finally { setLoading(false); }
  }

  // Actions: edit a rule in memory, save all, reset to defaults.
  function updateRule(id, patch) {
    setCfg(c => ({ ...c, rules: c.rules.map(r => r.id === id ? { ...r, ...patch } : r) }));
  }
  function updateRuleParams(id, paramPatch) {
    setCfg(c => ({
      ...c,
      rules: c.rules.map(r => r.id === id
        ? { ...r, params: { ...(r.params || {}), ...paramPatch } }
        : r)
    }));
  }

  async function save() {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await putJson('/api/settings/issue-rules', cfg);
      setMsg('Saved ' + r.ruleCount + ' rules.');
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  }

  async function resetToDefaults() {
    if (!window.confirm('Reset ALL issue rules to the bundled defaults? Your edits will be discarded.')) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await delJson('/api/settings/issue-rules');
      setMsg('Reset to defaults.');
      await reload();
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  }

  // Rules of Hooks: memoize BEFORE early returns. Guard the derefs so
  // cfg=null on first render doesn't crash the useMemo callback.
  const { simple, system } = useMemo(() => {
    const rules = (cfg && cfg.rules) || [];
    return {
      simple: rules.filter(r => !r.system),
      system: rules.filter(r =>  r.system)
    };
  }, [cfg]);

  if (loading) return <div className="card" style={{ padding: '0.75rem' }}>Loading issue rules...</div>;
  if (err && !cfg) return <div className="card" style={{ padding: '0.75rem', color: '#a00' }}>{err}</div>;
  if (!cfg) return null;

  // Screen: header with Save / Reset, simple rules table, system rules table.
  return (
    <div className="card" style={{ padding: '1rem', maxWidth: '68em' }}>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>Issue rules</h3>
        <button className="refresh-btn" disabled={busy} onClick={save}>Save changes</button>
        <button className="refresh-btn" style={{ background: '#6b7280' }} disabled={busy} onClick={resetToDefaults}>Reset to defaults</button>
        {msg && <span className="small" style={{ color: '#065f46' }}>{msg}</span>}
        {err && <span className="small" style={{ color: '#a00' }}>{err}</span>}
      </div>
      <p className="small" style={{ color: '#555', marginTop: 0 }}>
        These rules run on every Google-form row on import and on every re-check in the Review tab.
        Turn a rule off to stop it flagging rows. Change severity to promote a warning to critical (or demote).
        For rules with tunable numbers (thresholds, %), edit the value inline.
      </p>

      <h4 style={{ marginTop: '1rem', marginBottom: '0.4rem' }}>Simple rules</h4>
      {/* Simple rules: on/off, severity and editable thresholds. */}
      <table className="datatable" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: '3.5em' }}>On</th>
            <th>Rule</th>
            <th style={{ width: '7em' }}>Severity</th>
            <th>Description &amp; params</th>
            <th>Condition</th>
          </tr>
        </thead>
        <tbody>
          {simple.map(r => (
            <tr key={r.id}>
              <td><input type="checkbox" checked={!!r.enabled} onChange={e => updateRule(r.id, { enabled: e.target.checked })} /></td>
              <td><strong>{r.label}</strong><div className="small" style={{ color: '#888' }}>{r.id}</div></td>
              <td>
                <select value={r.severity || 'warning'} onChange={e => updateRule(r.id, { severity: e.target.value })}>
                  <option value="critical">critical</option>
                  <option value="warning">warning</option>
                </select>
              </td>
              <td className="small">
                <div style={{ color: '#333' }}>{r.description}</div>
                {r.params && <RuleParamsEditor rule={r} onChange={(patch) => updateRuleParams(r.id, patch)} />}
              </td>
              <td style={{ fontFamily: 'monospace', fontSize: '0.75em', color: '#555', maxWidth: '20em', whiteSpace: 'pre-wrap' }}>
                {formatCondition(r.condition, 0)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4 style={{ marginTop: '1.25rem', marginBottom: '0.4rem' }}>System rules (complex - read-only apart from on/off + severity)</h4>
      {/* System rules: built-in logic; only on/off and severity can change. */}
      <table className="datatable" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: '3.5em' }}>On</th>
            <th>Rule</th>
            <th style={{ width: '7em' }}>Severity</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {system.map(r => (
            <tr key={r.id}>
              <td><input type="checkbox" checked={!!r.enabled} onChange={e => updateRule(r.id, { enabled: e.target.checked })} /></td>
              <td><strong>{r.label}</strong><div className="small" style={{ color: '#888' }}>{r.id}</div></td>
              <td>
                <select value={r.severity || 'warning'} onChange={e => updateRule(r.id, { severity: e.target.value })}>
                  <option value="critical">critical</option>
                  <option value="warning">warning</option>
                </select>
              </td>
              <td className="small" style={{ color: '#333' }}>{r.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Small form for a rule's numeric parameters (thresholds).
function RuleParamsEditor({ rule, onChange }) {
  const p = rule.params || {};
  const keys = Object.keys(p);
  return (
    <div style={{ marginTop: '0.4rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
      {keys.map(k => {
        const v = p[k];
        if (Array.isArray(v)) {
          return (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <span className="small" style={{ color: '#666' }}>{k}:</span>
              <input
                type="text"
                value={v.join(', ')}
                onChange={e => onChange({ [k]: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                style={{ minWidth: '14em', fontSize: '0.83em' }}
              />
            </label>
          );
        }
        if (typeof v === 'number') {
          return (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <span className="small" style={{ color: '#666' }}>{k}:</span>
              <input
                type="number"
                value={v}
                onChange={e => onChange({ [k]: Number(e.target.value) })}
                style={{ width: '6em', fontSize: '0.83em' }}
                step="any"
              />
            </label>
          );
        }
        return (
          <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <span className="small" style={{ color: '#666' }}>{k}:</span>
            <input
              type="text"
              value={String(v == null ? '' : v)}
              onChange={e => onChange({ [k]: e.target.value })}
              style={{ minWidth: '10em', fontSize: '0.83em' }}
            />
          </label>
        );
      })}
    </div>
  );
}

// Pretty-print a condition tree so admins can see what a rule is doing.
// Turns a rule's condition into readable text for the table.
function formatCondition(cond, depth) {
  if (!cond) return '';
  const pad = '  '.repeat(depth);
  if (cond.op === 'AND' || cond.op === 'OR') {
    const inner = (cond.clauses || []).map(c => formatCondition(c, depth + 1)).join('\n');
    return pad + cond.op + '(\n' + inner + '\n' + pad + ')';
  }
  const rhs = cond.value_ref
    ? ' params.' + String(cond.value_ref).replace(/^params\./, '')
    : (cond.value === undefined ? '' : ' ' + JSON.stringify(cond.value));
  return pad + cond.field + ' ' + cond.operator + rhs;
}
